






























import { Component, Vue, Prop } from 'vue-property-decorator';
import {MediaSliderState} from '@share/presentation/types/MediaSliderState';
import {GoToParams} from "@share/presentation/types/GoToParams";
import {SliderInterface} from "@share/presentation/interfaces/SliderInterface";
import {SliderParams} from "@share/presentation/types/SliderParams";
import {ResizeHelper} from "@share/presentation/helpers/ResizeHelper";
import SliderArrow from "@share/presentation/components/SliderArrow.vue";
import {notCrossingLeftBorder, notCrossingRightBorder} from "@share/presentation/helpers/BorderHelper";
import ImageView from "@share/common/ImageView.vue";

@Component<MediaSlider>({
    name: "MediaSlider",
    components: {ImageView, SliderArrow}
})
export default class MediaSlider extends Vue {
    @Prop({ default: () => [] }) slides: string[];
    @Prop({ required: true }) sliderInstance: SliderInterface;
    @Prop({default: 0}) initIndex: number;
    @Prop({default: false}) readOnly: boolean;
    @Prop({default: false}) useArrows: boolean;

    private state: MediaSliderState = new MediaSliderState();
    private resizeHelper: ResizeHelper | null = null;
    private slideTouchFinished = true;

    get slidesLength(): number {
        return this.slides.length;
    }

    get slider(): HTMLElement {
        return this.$refs['slider'] as HTMLElement;
    }

    get slidesElements(): HTMLElement[] {
        return this.$refs['slides'] as HTMLElement[];
    }

    get styles(): Record<string, string> {
        return {
            left: this.state.offset + 'px'
        }
    }

    slideSize(): number {
        if (this.slidesElements && this.slidesElements.length) {
            return this.slidesElements[0].offsetWidth;
        }

        return 0;
    }

    totalSize(): number {
        if (this.slidesElements && this.slidesElements.length) {
            return this.slidesElements.reduce((accum, item) => accum+=item.offsetWidth, 0);
        }

        return 0;
    }

    sliderParams(): SliderParams {
        return {
            state: this.state,
            slideSize: this.slideSize(),
            slidesLength: this.slidesLength,
            totalSize: this.totalSize(),
        };
    }

    mounted() {
        this.updateSliderParams();
        this.resizeHelper = new ResizeHelper(this.$el as HTMLElement, this.sliderInstance, this.updateSliderParams);

        if (this.initIndex > 0) {
            this.goTo({
                index: this.initIndex,
                setIndex: true,
                withoutAnimation: true
            })
        }

        if (this.sliderInstance.isFluent) {
            (this.$el as HTMLElement).addEventListener('wheel', this.onWheel);
        }

        this.resizeHelper?.addResizeListener();

        if (this.useArrows) {
            document.addEventListener('keydown', this.keyDownEvent);
        }
    }

    // @Debounce(300)
    updateSliderParams() {
        this.sliderInstance.setParams(this.sliderParams());
    }

    toggleDocumentListeners(isAdding = true): void {
        if (isAdding) {
            document.onmouseup = this.dragEnd;
            document.onmousemove = this.dragAction;
        }
        else {
            document.onmouseup = null;
            document.onmousemove = null;
        }
    }

    dragStart(e: MouseEvent | TouchEvent): void {
        e = e || window.event;
        e.preventDefault();
        if (this.readOnly) {
            return;
        }

        this.state.posInitial = this.slider.offsetLeft;
        if (e.type == 'touchstart') {
            this.state.posx1 = (e as TouchEvent).touches[0].clientX;
        }
        else {
            this.state.posx1 = (e as MouseEvent).clientX;
            this.toggleDocumentListeners();
        }
        this.state.isMoving = false;
    }

    dragEnd(): void {
        if (this.readOnly) {
            return;
        }
        const indexBeforeMove = this.state.index;

        this.state.posFinal = this.slider.offsetLeft;
        const goToParams = this.sliderInstance.dragEnd(this.slider.offsetLeft);

        if (Object.keys(goToParams).length) {
            this.goTo(goToParams);

            if (this.state.index != indexBeforeMove) {
                this.emitMoved();
            }
        }

        this.toggleDocumentListeners(false);
    }

    dragAction(e: MouseEvent | TouchEvent): void {
        if (this.readOnly) {
            return;
        }

        this.state.isMoving = true;
        e = e || window.event;

        const clientX = e.type == 'touchmove'
            ? (e as TouchEvent).touches[0].clientX
            : (e as MouseEvent).clientX;

        this.state.posx2 = this.state.posx1 - clientX;
        this.state.posx1 = clientX;

        //@todo come up with something more witty
        const isNotCrossingLeftBorder = notCrossingLeftBorder(this.slider.offsetLeft, this.state.threshold);
        const isNotCrossingRightBorder = notCrossingRightBorder(
            this.slider.offsetLeft,
            this.sliderInstance.params.totalSize,
            this.sliderInstance.params.slideSize,
            this.state.threshold
        );

        if (isNotCrossingLeftBorder && isNotCrossingRightBorder) {
            this.state.offsetToDelta(this.slider.offsetLeft);
        }
    }

    onWheel(e: WheelEvent) {
        e.preventDefault();
        const newOffset = this.state.offset - e.deltaY;

        const isNotCrossingLeftBorder = notCrossingLeftBorder(newOffset);
        const isNotCrossingRightBorder = notCrossingRightBorder(
            newOffset,
            this.sliderInstance.params.totalSize,
            this.sliderInstance.params.slideSize,
        );

        if (isNotCrossingLeftBorder && isNotCrossingRightBorder) {
            this.state.offset = newOffset;
        }
    }

    next() {
        this.move(1);
    }

    previous() {
        this.move(-1);
    }

    move(direction: 1 | -1) {
        const indexBeforeMove = this.state.index;
        this.goTo(this.sliderInstance.shiftSlide(direction, this.slider.offsetLeft));

        //@toto make it common
        if (this.state.index != indexBeforeMove) {
            this.emitMoved();
        }
    }

    emitMoved(): void {
        if (this.sliderInstance.needToEmitMoved) {
            this.$emit('moved', this.state.index);
        }
    }

    transitionend(): void {
        this.state.isShifting = false;
        this.state.allowShift = true;
    }

    goTo(params: GoToParams): void {
        if (!params.withoutAnimation) {
            this.state.isShifting = true;
        }

        if (params.index !== undefined) {
            this.state.offset = -(params.index) * this.sliderInstance.params.slideSize;
        }
        if (params.offset !== undefined) {
            this.state.offset = params.offset;
        }

        if (params.setIndex && params.index != undefined) {
            this.state.index = params.index;
            this.state.activeIndex = params.index;
        }
    }

    onSlideClick(index: number): void {
        console.log('click');
        if (this.state.isMoving) {
            this.state.isMoving = false;
            return;
        }

        const needToBeSkipped = !this.sliderInstance.isClickable || this.readOnly || this.state.activeIndex == index;
        if (needToBeSkipped) {
            return;
        }

        this.state.activeIndex = index;
        this.$emit('selected', index);

        this.goTo({ index });
    }

    keyDownEvent(e: KeyboardEvent) {
        if (e.key == 'Left' || e.key == 'ArrowLeft') {
            this.previous();
        }
        else if (e.key == 'Right' || e.key == 'ArrowRight') {
            this.next();
        }
    }

    destroyed() {
        this.resizeHelper?.removeResizeListener();
        if (this.sliderInstance.isFluent) {
            (this.$el as HTMLElement).addEventListener('wheel', this.onWheel);
        }
        if (this.useArrows) {
            document.removeEventListener('keydown', this.keyDownEvent);
        }
    }

    slideTouchStart(index: number) {
        this.slideTouchFinished = false;
        setTimeout(() => {
            if (this.slideTouchFinished) {
                this.onSlideClick(index);
            }
        }, 100)
    }

    slideTouchEnd() {
        this.slideTouchFinished = true;
    }
}
